<template>
  <div class="doc-page">
    <section v-loading="loading" class="doc-card">
      <button class="back-btn" @click="$router.push({ name: 'home' })">Back to Home</button>
      <span class="badge">{{ doc.version || 'v1.0' }}</span>
      <h1>{{ doc.title || 'RoboCoin Open API Integration Guide' }}</h1>
      <p class="summary">{{ doc.summary || 'API reference for integrating third-party systems with RoboCoin.' }}</p>
      <p v-if="loadWarning" class="load-warning">{{ loadWarning }}</p>
      <pre>{{ doc.content || 'No documentation is available.' }}</pre>
    </section>
  </div>
</template>

<script>
import { getOpenApiDoc } from '../api/support'
import { localeState } from '../utils/i18n'

const DEFAULT_OPEN_API_DOC = {
  title: 'RoboCoin Open API Integration Guide',
  version: 'v1.0',
  summary: 'Account, task publishing, robot registration, dispatch status, and SDK binding reference for third-party systems.',
  content: `# RoboCoin Open API Integration Guide

## 1. Authentication
Third-party systems must use the Open API token generated after user sign-in.

Request headers:
\`\`\`http
Authorization: Bearer <token>
Content-Type: application/json
\`\`\`

## 2. Publish a Task
Endpoint: POST /open-api/tasks/publish

Common fields:
- taskName: optional task name
- title: required task title
- message: required task description
- serviceType: HOME_SERVICE or SHUTTLE
- expectedRobotType: CLEANING, DELIVERY, SHUTTLE, or INSPECTION
- serviceAddress, serviceLat, serviceLng: on-site service address and GPS coordinates
- beginAddress, beginLat, beginLng: shuttle start point and GPS coordinates
- endAddress, endLat, endLng: shuttle destination and GPS coordinates
- contactPhone: contact phone number
- scheduleTime: scheduled time in yyyy-MM-dd HH:mm:ss format

Response fields:
- taskId: task ID
- dispatchStatus: DeRAS dispatch status
- estimatedRobocoin: estimated platform subsidy

## 3. Register a Robot
Endpoint: POST /open-api/robots/register

Common fields:
- name: robot name
- category: robot category
- contactPhone: contact phone number
- countryCode: international country code
- walAddress: optional payment wallet address
- capabilityDesc: capability description
- regionLimitLevel: optional regional restriction level
- regionLimitName: optional regional restriction name

## 4. Query the Task Market
Endpoint: GET /open-api/tasks

Optional parameters:
- robotType: robot type filter
- serviceType: service type filter
- status: task status filter

## 5. Accept a Task
Endpoint: POST /open-api/tasks/{taskId}/accept

The platform uses an atomic database lock so only one robot can accept a task. If another robot has already locked it, the API returns an acceptance error.

## 6. Dispatch Status
- SEARCHING: DeRAS is finding a robot
- PENDING: a candidate was invited and confirmation is pending
- ACCEPTED: a robot accepted and locked the task
- REJECTED: the candidate declined and dispatch continues
- EXPIRED: the candidate timed out and dispatch continues
- OPEN_GRAB: no candidate is available and open acceptance is enabled

## 7. Trusted SDK Binding
After robot registration, the platform generates an SDK installation command. The SDK submits a TPM-signed report from the controller, and successful verification starts the binding confirmation flow.

## 8. Error Format
All endpoints return:
\`\`\`json
{
  "code": 200,
  "msg": "Request completed successfully.",
  "data": {}
}

\`\`\`

When code is not 200, display msg and stop the current operation.`
}

const DEFAULT_OPEN_API_DOC_EN = {
  title: 'RoboCoin Open API Integration Guide',
  version: 'v1.0',
  summary: 'Integration guidance for accounts, task publishing, robot registration, dispatch status, and SDK binding.',
  content: `# RoboCoin Open API Integration Guide

## 1. Authentication
Third-party systems must use the Open API Token generated after user sign-in.

Request headers:
\`\`\`http
Authorization: Bearer <token>
Content-Type: application/json
\`\`\`

## 2. Publish a Task
Endpoint: POST /open-api/tasks/publish

Common fields:
- taskName: Optional task name
- title: Required task title
- message: Required task description
- serviceType: HOME_SERVICE for on-site service or SHUTTLE for shuttle service
- expectedRobotType: Expected robot type, such as CLEANING, DELIVERY, SHUTTLE, or INSPECTION
- serviceAddress, serviceLat, serviceLng: On-site service address and GPS coordinates
- beginAddress, beginLat, beginLng: Shuttle pickup and GPS coordinates
- endAddress, endLat, endLng: Shuttle destination and GPS coordinates
- contactPhone: Contact phone number
- scheduleTime: Scheduled time in yyyy-MM-dd HH:mm:ss format

Response fields:
- taskId: Task ID
- dispatchStatus: DeRAS dispatch status
- estimatedRobocoin: Estimated platform subsidy

## 3. Register a Robot
Endpoint: POST /open-api/robots/register

Common fields:
- name: Robot name
- category: Robot category
- contactPhone: Contact phone number
- countryCode: Country calling code
- walAddress: Optional robot payment wallet address
- capabilityDesc: Capability description
- regionLimitLevel: Optional region restriction level
- regionLimitName: Optional region restriction name

## 4. Query the Task Market
Endpoint: GET /open-api/tasks

Optional parameters:
- robotType: Filter by robot type
- serviceType: Filter by service type
- status: Filter by task status

## 5. Accept a Task
Endpoint: POST /open-api/tasks/{taskId}/accept

The backend uses an atomic database lock so only one robot can accept a task. If another robot has already locked it, the API returns an acceptance failure message.

## 6. Dispatch Status
Common values:
- SEARCHING: DeRAS is finding robots
- PENDING: Candidate robots were invited and confirmation is pending
- ACCEPTED: A robot accepted and locked the task
- REJECTED: A candidate declined and dispatch continues
- EXPIRED: A candidate timed out and dispatch continues
- OPEN_GRAB: No candidate was found; provider acceptance is open

## 7. Trusted SDK Binding
After robot registration, the platform generates an SDK installation command. The SDK submits a TPM-signed report from the industrial computer, and binding confirmation starts after platform verification.

## 8. Error Format
All endpoints return:
\`\`\`json
{
  "code": 200,
  "msg": "Success",
  "data": {}
}
\`\`\`

When code is not 200, the caller should display msg and stop the current operation.`
}

export default {
  name: 'OpenApiDoc',
  data() {
    return {
      loading: false,
      loadWarning: '',
      doc: { ...DEFAULT_OPEN_API_DOC },
      usesDefaultDoc: true
    }
  },
  computed: {
    displayDoc() {
      if (this.usesDefaultDoc && localeState.locale === 'en') return DEFAULT_OPEN_API_DOC_EN
      return this.doc
    }
  },
  created() {
    this.fetchDoc()
  },
  methods: {
    async fetchDoc() {
      this.loading = true
      try {
        const response = await getOpenApiDoc()
        if (response.data.code === 200) {
          const remoteDoc = response.data.data
          this.doc = remoteDoc && remoteDoc.content && !/[\u3400-\u9fff]/.test(`${remoteDoc.title || ''}${remoteDoc.summary || ''}${remoteDoc.content}`)
            ? remoteDoc
            : { ...DEFAULT_OPEN_API_DOC }
          this.loadWarning = ''
        } else {
          this.usesDefaultDoc = true
          this.doc = { ...DEFAULT_OPEN_API_DOC }
          this.loadWarning = 'The API document is not published. Showing the built-in reference.'
          this.$message.warning(this.loadWarning)
        }
      } catch (error) {
        this.usesDefaultDoc = true
        this.doc = { ...DEFAULT_OPEN_API_DOC }
        this.loadWarning = 'Failed to load the API document. Showing the built-in reference.'
        this.$message.warning(this.loadWarning)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.doc-page {
  min-height: 100vh;
  padding: 32px;
  color: #102033;
  background: #f5f8fc;
}

.doc-card {
  max-width: 1060px;
  margin: 0 auto;
  padding: 34px;
  border-radius: 22px;
  background: #fff;
  border: 1px solid #d8e3ee;
  box-shadow: 0 16px 36px rgba(16, 32, 51, 0.08);
}

.back-btn {
  border: 1px solid #d8e3ee;
  border-radius: 999px;
  padding: 9px 13px;
  color: #102033;
  cursor: pointer;
  background: #fff;
}

.badge {
  display: inline-flex;
  margin-left: 10px;
  padding: 8px 11px;
  border-radius: 999px;
  color: #0f77ff;
  background: rgba(15, 119, 255, 0.08);
  font-weight: 800;
}

h1 {
  margin: 22px 0 12px;
}

.summary {
  color: #58708d;
  line-height: 1.8;
}

.load-warning {
  padding: 12px 14px;
  color: #8a4b00;
  border-radius: 12px;
  background: #fff7df;
  border: 1px solid #ffe0a3;
}

pre {
  margin-top: 22px;
  padding: 22px;
  white-space: pre-wrap;
  line-height: 1.8;
  border-radius: 18px;
  background: #f7fafc;
  border: 1px solid #d8e3ee;
}
</style>
